import {
  FileText,
  GraduationCap,
  Keyboard,
  LayoutDashboard,
  RefreshCw,
  Search,
  Settings,
  SlidersHorizontal,
  User,
  Users,
} from "lucide-react";
import { CanAccess, useGetList, useTranslate, useUserMenu } from "ra-core";
import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { ThemeModeToggle } from "@/components/admin/theme-mode-toggle";
import { UserMenu } from "@/components/admin/user-menu";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";

import { useConfigurationContext } from "../root/ConfigurationContext";
import { useSignertUrl } from "../providers/commons/useSignertVedlegg";
import type { Company } from "../types";
import { TripletexSyncDialog } from "./TripletexSyncDialog";
import { ActiveTimerIndicator } from "./ActiveTimerIndicator";
import { routePaths } from "../root/routePaths";
import { Kbd } from "./Kbd";
import {
  isOldLayout,
  oldLayoutAvailable,
  useDashboardPrefs,
} from "../dashboard/focus/useDashboardPrefs";
import { isMac, OPEN_SHORTCUTS_EVENT } from "./shortcuts";

// Åpner Cmd+K-paletten via en egen hendelse som CommandPalette lytter på.
const openCommandPalette = () => {
  window.dispatchEvent(new Event("mimin:open-command"));
};

// Topp-bar: logo + navn til venstre, søk i midten, tema-bryter + bruker-meny til
// høyre.
export const TopBar = () => {
  const { darkModeLogo, title } = useConfigurationContext();
  // Logoen kan være lastet opp til den private vedleggsbøtta; da må pekeren
  // byttes med en signert lenke, ellers vises et brutt bilde. Medfølgende
  // logoer (/logos/…) går uendret gjennom.
  const logoSrc = useSignertUrl(darkModeLogo);
  const translate = useTranslate();
  // Logoen tar brukeren til «Min bedrift» (egen bedrift-kort). Faller tilbake
  // til Oversikt mens den lastes / om den mangler.
  const { data: ownCompany } = useGetList<Company>("companies", {
    filter: { is_own_company: true },
    pagination: { page: 1, perPage: 1 },
  });
  const ownCompanyId = ownCompany?.[0]?.id;
  const logoTo = ownCompanyId ? `/companies/${ownCompanyId}/show` : "/";
  return (
    <header className="flex h-12 shrink-0 items-center gap-3 border-b px-3">
      <Link
        to={logoTo}
        className="flex shrink-0 items-center gap-2 no-underline text-foreground"
      >
        {logoSrc && <img src={logoSrc} alt={title} className="h-6 w-auto" />}
        <span className="text-base font-semibold">{title}</span>
      </Link>
      <button
        type="button"
        onClick={openCommandPalette}
        className="mx-auto flex w-full max-w-md items-center gap-2 rounded-md border bg-muted/40 px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted"
      >
        <Search className="h-4 w-4" />
        <span className="truncate">
          {translate("crm.command.placeholder", { _: "Søk eller naviger..." })}
        </span>
        <span className="ml-auto hidden shrink-0 gap-1 sm:flex">
          <Kbd>{isMac ? "⌘" : "Ctrl"}</Kbd>
          <Kbd>K</Kbd>
        </span>
      </button>
      <div className="flex shrink-0 items-center gap-1">
        <ActiveTimerIndicator />
        <ThemeModeToggle />
        <UserMenu>
          <ProfileMenu />
          <CanAccess resource="sales" action="list">
            <UsersMenu />
          </CanAccess>
          <CanAccess resource="configuration" action="edit">
            <SettingsMenu />
          </CanAccess>
          <CanAccess resource="field_defs" action="list">
            <CustomFieldsMenuItem />
          </CanAccess>
          <TripletexSyncMenuItem />
          <DashboardLayoutMenuItem />
          <TutorialMenuItem />
          <ShortcutsMenuItem />
          <ChangelogMenuItem />
        </UserMenu>
      </div>
    </header>
  );
};

const UsersMenu = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<UsersMenu> must be used inside <UserMenu>");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/sales" className="flex items-center gap-2">
        <Users />
        {translate("resources.sales.name", { smart_count: 2 })}
      </Link>
    </DropdownMenuItem>
  );
};

const ProfileMenu = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<ProfileMenu> must be used inside <UserMenu>");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/profile" className="flex items-center gap-2">
        <User />
        {translate("crm.profile.title")}
      </Link>
    </DropdownMenuItem>
  );
};

const SettingsMenu = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<SettingsMenu> must be used inside <UserMenu>");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/settings" className="flex items-center gap-2">
        <Settings />
        {translate("crm.settings.title")}
      </Link>
    </DropdownMenuItem>
  );
};

const CustomFieldsMenuItem = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<CustomFieldsMenuItem> must be used inside <UserMenu>");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/field_defs" className="flex items-center gap-2">
        <SlidersHorizontal />
        {translate("crm.custom_fields.manage", { _: "Egendefinerte felt" })}
      </Link>
    </DropdownMenuItem>
  );
};

// Synk stamdata (kunder, kontakter, produkter) fra Tripletex – Tripletex er
// master, Mimin oppdaterer aldri Tripletex. Admin-gatet i edge-funksjonen;
// andre brukere får en forklarende feilmelding.
const TripletexSyncMenuItem = () => {
  const translate = useTranslate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<TripletexSyncMenuItem> must be used inside <UserMenu>");
  }

  return (
    <>
      <DropdownMenuItem
        onSelect={(event) => {
          event.preventDefault();
          setDialogOpen(true);
        }}
        className="flex items-center gap-2"
      >
        <RefreshCw />
        {translate("crm.tripletex_sync.menu", { _: "Synk fra Tripletex" })}
      </DropdownMenuItem>
      {dialogOpen && (
        <TripletexSyncDialog
          open={dialogOpen}
          onClose={() => {
            setDialogOpen(false);
            userMenuContext.onClose();
          }}
        />
      )}
    </>
  );
};

// «Gammel oversikt» i to uker etter at Oversikt «Fokus» kom (kap. 16).
const DashboardLayoutMenuItem = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  const { layout, save } = useDashboardPrefs();
  const navigate = useNavigate();
  if (!userMenuContext) {
    throw new Error("<DashboardLayoutMenuItem> must be used inside <UserMenu>");
  }
  if (!oldLayoutAvailable()) return null;
  const old = isOldLayout(layout);
  return (
    <DropdownMenuItem
      onClick={() => {
        userMenuContext.onClose();
        save({ layout: old ? "fokus" : "gammel" });
        navigate("/");
      }}
      className="flex items-center gap-2"
    >
      <LayoutDashboard />
      {old
        ? translate("crm.dashboard.new_layout", { _: "Ny oversikt" })
        : translate("crm.dashboard.old_layout", { _: "Gammel oversikt" })}
    </DropdownMenuItem>
  );
};

const TutorialMenuItem = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<TutorialMenuItem> must be used inside <UserMenu>");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to={routePaths.tutorial} className="flex items-center gap-2">
        <GraduationCap />
        {translate("crm.tutorial.title")}
      </Link>
    </DropdownMenuItem>
  );
};

const ShortcutsMenuItem = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<ShortcutsMenuItem> must be used inside <UserMenu>");
  }
  return (
    <DropdownMenuItem
      onClick={() => {
        userMenuContext.onClose();
        window.dispatchEvent(new Event(OPEN_SHORTCUTS_EVENT));
      }}
      className="flex items-center gap-2"
    >
      <Keyboard />
      {translate("crm.shortcuts.title", { _: "Tastatursnarveier" })}
      <span className="ml-auto">
        <Kbd>?</Kbd>
      </span>
    </DropdownMenuItem>
  );
};

const ChangelogMenuItem = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<ChangelogMenuItem> must be used inside <UserMenu>");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to={routePaths.changelog} className="flex items-center gap-2">
        <FileText />
        {translate("crm.changelog.title")}
      </Link>
    </DropdownMenuItem>
  );
};
